import * as Sentry from "@sentry/react";
import { VITE_PUBLIC_SUPABASE_URL, VITE_PUBLIC_SUPABASE_ANON_KEY } from "@common/utils/constants";
import { classifyNetworkError, notifyNetworkError } from "@common/lib/sentryUtils";

const supabaseUrl = VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = VITE_PUBLIC_SUPABASE_ANON_KEY;

// Lazy-load supabase client to avoid circular dependency
let supabaseClient: any = null;
const getSupabase = async () => {
    if (!supabaseClient) {
        const module = await import("./supabaseClient");
        supabaseClient = module.supabase;
    }
    return supabaseClient;
};

// Error handling wrapper function with retry logic
export async function retryWrapper<T>(
    func: () => Promise<{ data: T | null; error: any }>,
): Promise<{ data: T | null; error: any }> {
    let retries = 0;
    const maxRetries = 3;
    let delay = 100; // Initial delay in ms

    while (retries < maxRetries) {
        try {
            const { data, error } = await func();

            if (error) {
                // Handle AbortError (code 20), PGRST116 (no rows returned), and fetch aborts
                if (
                    error?.code === "20" ||
                    error?.code === "PGRST116" ||
                    (error?.message && error?.message?.includes("AbortError"))
                ) {
                    // Do nothing if error is due to aborting the fetch
                    return { data: null, error: null };
                }

                // Handle JWT expired
                if (error?.message && isUnAuthorizedError(error)) {
                    Sentry.addBreadcrumb({
                        category: "auth",
                        message: `[Supabase Auth Error] JWT expired, attempting refresh`,
                        level: "warning",
                        data: {
                            error: error.message,
                            code: error.code,
                            timestamp: new Date().toISOString(),
                        },
                    });

                    // Try to refresh session
                    const supabase = await getSupabase();
                    const { error: refreshError } = await supabase.auth.refreshSession();
                    if (refreshError) {
                        const isInvalidToken =
                            refreshError.message?.toLowerCase().includes("refresh token not found") ||
                            refreshError.message?.toLowerCase().includes("invalid refresh token");

                        const errorForSentry = createErrorFromSupabaseError(refreshError);
                        Sentry.captureException(errorForSentry, {
                            tags: {
                                errorType: isInvalidToken
                                    ? "supabase_invalid_refresh_token"
                                    : "supabase_auth_refresh_failed",
                                errorCategory: "auth",
                            },
                            extra: {
                                timestamp: new Date().toISOString(),
                            },
                        });

                        // Can't refresh session, redirect to login. Local scope only — refresh
                        // failure on this device shouldn't kick the user off other devices.
                        // Inlined (rather than signOutLocal) because of a circular dep:
                        // supabaseClient -> retryWrapper -> signOut -> supabaseClient.
                        const { error: signOutError } = await supabase.auth.signOut({ scope: "local" });
                        if (signOutError) {
                            Sentry.captureException(signOutError, {
                                tags: { errorCategory: "auth", operation: "signOutLocal", site: "retryWrapper" },
                            });
                        }
                        window.location.href = "/";
                        return { data: null, error };
                    } else {
                        Sentry.addBreadcrumb({
                            category: "auth",
                            message: `[Supabase Auth Error] Session refreshed successfully, retrying operation`,
                            level: "info",
                            data: {
                                timestamp: new Date().toISOString(),
                            },
                        });
                        // Session refreshed, send to catch block to retry the operation with backoff
                        throw error;
                    }
                }

                // Handle supabase network errors
                const networkCategory = error?.message ? classifyNetworkError(error) : "code_bug";
                if (networkCategory !== "code_bug") {
                    // Log to Sentry with additional context
                    const errorForSentry = createErrorFromSupabaseError(error);
                    Sentry.captureException(errorForSentry, {
                        tags: {
                            errorType: "supabase_network_error",
                            errorCategory: networkCategory,
                            retryAttempt: retries + 1,
                            maxRetries: maxRetries,
                        },
                        extra: {
                            supabaseUrl: supabaseUrl,
                            hasAnonKey: !!supabaseAnonKey,
                            userAgent: navigator.userAgent,
                            online: navigator.onLine,
                            connectionType: (navigator as any).connection?.effectiveType || "unknown",
                        },
                    });
                    // Retry with exponential backoff
                    throw error; // Throw to catch block
                }

                // For non-network errors, return immediately
                return { data: null, error };
            }

            // Success case
            return { data, error: null };
        } catch (error: any) {
            // Supabase throws (not returns) AbortError when .abortSignal() fires.
            // AuthRetryableFetchError is thrown for both abort signals AND real network
            // failures during auth operations. Check the message to distinguish:
            // intentional abort signals should stop silently, but real network errors
            // should retry with backoff and logging.
            if (error?.name === "AbortError") {
                return { data: null, error: null };
            }
            if (error?.name === "AuthRetryableFetchError") {
                const errorMessage = error?.message?.toLowerCase() || "";
                // Only treat as intentional cancellation if message mentions "abort"
                if (errorMessage.includes("abort")) {
                    return { data: null, error: null };
                }
                // Otherwise, fall through to retry logic for genuine network failures
            }

            retries++;

            // Enhanced error logging for caught exceptions
            const exceptionCategory = error?.message ? classifyNetworkError(error) : "code_bug";
            if (exceptionCategory !== "code_bug") {
                // Log to Sentry with additional context
                const errorForSentry = createErrorFromSupabaseError(error);
                Sentry.captureException(errorForSentry, {
                    tags: {
                        errorType: "supabase_network_exception",
                        errorCategory: exceptionCategory,
                        retryAttempt: retries,
                        maxRetries: maxRetries,
                    },
                    extra: {
                        supabaseUrl: supabaseUrl,
                        hasAnonKey: !!supabaseAnonKey,
                        userAgent: navigator.userAgent,
                        online: navigator.onLine,
                        connectionType: (navigator as any).connection?.effectiveType || "unknown",
                        timestamp: new Date().toISOString(),
                    },
                });
            }

            if (retries >= maxRetries) {
                const finalCategory = classifyNetworkError(error);
                Sentry.captureException(error, {
                    tags: {
                        errorType: "supabase_max_retries_reached",
                        errorCategory: finalCategory,
                        maxRetries: maxRetries,
                    },
                    extra: {
                        finalError: error.message,
                        code: error.code,
                        online: navigator.onLine,
                        connectionType: (navigator as any).connection?.effectiveType ?? "unknown",
                        timestamp: new Date().toISOString(),
                    },
                });

                notifyNetworkError(finalCategory);

                return { data: null, error };
            }

            // Wait before retrying with exponential backoff
            await new Promise((resolve) => setTimeout(resolve, delay));
            // Exponential backoff (with jitter) 4-6 times the previous delay (delays 100ms, then up to 600ms, 3600ms)
            delay = Math.floor(delay * (Math.random() * 2 + 4));
        }
    }

    return { data: null, error: new Error("Unexpected error in retryWrapper") };
}

interface SupabaseError extends Error {
    code?: string;
    details?: string;
    hint?: string;
}

export function createErrorFromSupabaseError(supabaseError: any): SupabaseError {
    // Create a proper Error object with Supabase error details
    const error = new Error(supabaseError?.message || "Unknown Supabase error") as SupabaseError;

    // Add Supabase-specific properties to the error object
    if (supabaseError?.code) {
        error.code = supabaseError.code;
    }
    if (supabaseError?.details) {
        error.details = supabaseError.details;
    }
    if (supabaseError?.hint) {
        error.hint = supabaseError.hint;
    }

    return error;
}

function isUnAuthorizedError(error: any): boolean {
    const msg = error?.message?.toLowerCase() ?? "";
    return (
        msg.includes("unauthorized") ||
        msg.includes("jwt expired") ||
        msg.includes("jwt invalid") ||
        msg.includes("jwt malformed") ||
        msg.includes("jwt not yet valid") ||
        msg.includes("401")
    );
}
